import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ChefHat, 
  Utensils, 
  Calendar, 
  Users, 
  Menu,
  ArrowRight,
  Star,
  Clock,
  MapPin,
  Phone,
  Sparkles,
  Heart,
  Zap,
  Award,
  MessageCircle
} from 'lucide-react';
import GoogleMapLocator from '../components/GoogleMapLocator';

const LandingPageNew = () => {
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleDirectOrder = () => {
    navigate('/order');
  };

  const handleZomatoOrder = () => {
    window.open('https://link.zomato.com/xqzv/rshare?id=5385828530563b9f', '_blank');
  };

  const handleSwiggyOrder = () => {
    window.open('https://www.swiggy.com/direct/brand/464992?source=swiggy-direct&subSource=generic', '_blank');
  };

  const handleDailySubscription = () => {
    alert('Daily Food Subscription module coming soon!');
  };

  const handlePartyOrder = () => {
    alert('Party Order module coming soon!');
  };

  const handleFoodMenu = () => {
    navigate('/menu');
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/918107436861', '_blank');
  };

  const handleCall = () => {
    window.open('tel:+918107436861', '_self');
  };

  const options = [
    {
      id: 1,
      title: 'Direct Order',
      description: 'Order directly from our kitchen',
      icon: ChefHat,
      color: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)',
      action: handleDirectOrder,
      features: ['Fresh Food', 'Best Prices', 'Direct from Kitchen']
    },
    {
      id: 2,
      title: 'Order from Zomato',
      description: 'Fast delivery via Zomato',
      icon: () => <img src="https://upload.wikimedia.org/wikipedia/commons/7/75/Zomato_logo.png" alt="Zomato" style={{ width: window.innerWidth <= 768 ? '24px' : '32px', height: window.innerWidth <= 768 ? '24px' : '32px' }} />,
      color: 'linear-gradient(135deg, #ef4444 0%, #ec4899 100%)',
      action: handleZomatoOrder,
      features: ['Fast Delivery', 'Easy Payment', 'Track Order']
    },
    {
      id: 3,
      title: 'Order from Swiggy',
      description: 'Quick delivery via Swiggy',
      icon: () => <img src="https://upload.wikimedia.org/wikipedia/en/thumb/2/2d/Swiggy_logo.svg/1200px-Swiggy_logo.svg.png" alt="Swiggy" style={{ width: window.innerWidth <= 768 ? '24px' : '32px', height: window.innerWidth <= 768 ? '24px' : '32px' }} />,
      color: 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)',
      action: handleSwiggyOrder,
      features: ['Quick Delivery', 'Live Tracking', 'Easy Returns']
    },
    {
      id: 4,
      title: 'Daily Subscription',
      description: 'Subscribe for daily meals',
      icon: Calendar,
      color: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)',
      action: handleDailySubscription,
      features: ['Regular Delivery', 'Fresh Meals', 'Subscription Plans']
    },
    {
      id: 5,
      title: 'Party Order',
      description: 'Special party packages',
      icon: Users,
      color: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
      action: handlePartyOrder,
      features: ['Bulk Orders', 'Party Packages', 'Custom Menu']
    },
    {
      id: 6,
      title: 'Food Menu',
      description: 'Browse our complete menu',
      icon: Menu,
      color: 'linear-gradient(135deg, #3b82f6 0%, #0ea5e9 100%)',
      action: handleFoodMenu,
      features: ['Complete Menu', 'Prices', 'Descriptions']
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f8f9fa 100%)',
      color: '#333',
      fontFamily: 'Inter, sans-serif',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Light Food-Friendly Background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden'
      }}>
        {/* Subtle Floating Orbs */}
        <div style={{
          position: 'absolute',
          top: '80px',
          left: '80px',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          filter: 'blur(60px)',
          background: 'radial-gradient(circle, rgba(251, 146, 60, 0.08) 0%, rgba(239, 68, 68, 0.05) 100%)',
          animation: 'pulse 6s ease-in-out infinite'
        }}></div>
        <div style={{
          position: 'absolute',
          top: '160px',
          right: '80px',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          filter: 'blur(60px)',
          background: 'radial-gradient(circle, rgba(34, 197, 94, 0.08) 0%, rgba(16, 185, 129, 0.05) 100%)',
          animation: 'pulse 6s ease-in-out infinite 2s'
        }}></div>
        
        {/* Subtle Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.01) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.01) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      {/* Clean Food-Friendly Header */}
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backgroundColor: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: window.innerWidth <= 768 ? '16px 12px' : '24px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: window.innerWidth <= 768 ? '12px' : '16px' }}>
            <div style={{ position: 'relative' }}>
              <div style={{
                padding: window.innerWidth <= 768 ? '12px' : '16px',
                borderRadius: '16px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)',
                transition: 'all 0.5s ease',
                transform: 'scale(1)'
              }}>
                <ChefHat size={window.innerWidth <= 768 ? 24 : 32} color="white" />
              </div>
              <div style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                width: '12px',
                height: '12px',
                background: '#4ade80',
                borderRadius: '50%',
                animation: 'pulse 2s ease-in-out infinite'
              }}></div>
            </div>
            <div>
              <h1 style={{
                fontSize: window.innerWidth <= 768 ? '20px' : '30px',
                fontWeight: '900',
                color: '#1f2937',
                letterSpacing: '-0.025em',
                margin: 0
              }}>B.Tech Bawarchi</h1>
              <p style={{
                fontSize: window.innerWidth <= 768 ? '12px' : '14px',
                fontWeight: '500',
                color: '#f97316',
                margin: 0
              }}>Authentic Indian Cuisine</p>
            </div>
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px' 
          }}>
            {/* WhatsApp Button */}
            <button
              onClick={handleWhatsApp}
              style={{
                backgroundColor: '#25D366',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(37, 211, 102, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 211, 102, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(37, 211, 102, 0.3)';
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
              </svg>
            </button>

            {/* Call Button */}
            <button
              onClick={handleCall}
              style={{
                backgroundColor: '#ff6b35',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 107, 53, 0.3)';
              }}
            >
              <Phone size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section - Clean Introduction */}
      <section style={{
        padding: window.innerWidth <= 768 ? '24px 16px' : '40px 16px',
        position: 'relative'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <div style={{
            marginBottom: window.innerWidth <= 768 ? '32px' : '40px',
            transition: 'all 1s ease',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
          }}>
            {/* Main Heading */}
            <h2 style={{
              fontSize: window.innerWidth <= 768 ? '28px' : '36px',
              fontWeight: '700',
              color: '#1f2937',
              margin: '0 0 12px 0',
              letterSpacing: '-0.025em',
              lineHeight: '1.2'
            }}>
              Welcome to{' '}
              <span style={{
                color: '#f97316'
              }}>
                B.Tech
              </span>{' '}
              <span style={{
                color: '#ef4444'
              }}>
                Bawarchi
              </span>
            </h2>
            
            {/* Subtitle */}
            <p style={{
              fontSize: window.innerWidth <= 768 ? '14px' : '16px',
              color: '#6b7280',
              margin: '0 auto 24px auto',
              maxWidth: window.innerWidth <= 768 ? '100%' : '500px',
              lineHeight: '1.5',
              fontWeight: '400'
            }}>
              Experience authentic Indian cuisine delivered through multiple premium channels.
            </p>
            
            {/* Stats */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: window.innerWidth <= 768 ? '16px' : '24px',
              flexWrap: 'wrap'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#6b7280',
                background: 'white',
                padding: window.innerWidth <= 768 ? '8px 12px' : '10px 16px',
                borderRadius: '20px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <Star size={16} color="#f59e0b" fill="currentColor" />
                <span style={{ fontSize: '14px', fontWeight: '600' }}>4.8 Rating</span>
              </div>
              <div style={{
                width: '4px',
                height: '4px',
                background: '#f97316',
                borderRadius: '50%'
              }}></div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#6b7280',
                background: 'white',
                padding: window.innerWidth <= 768 ? '8px 12px' : '10px 16px',
                borderRadius: '20px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <Clock size={16} color="#10b981" />
                <span style={{ fontSize: '14px', fontWeight: '600' }}>25 Min Delivery</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Order Options - Clean 2x2 Grid */}
      <section style={{
        padding: window.innerWidth <= 768 ? '0 16px 24px 16px' : '0 16px 40px 16px',
        position: 'relative'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: window.innerWidth <= 768 ? '24px' : '32px',
            transition: 'all 1s ease 0.5s',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
          }}>
            <h3 style={{
              fontSize: window.innerWidth <= 768 ? '20px' : '24px',
              fontWeight: '700',
              color: '#1f2937',
              margin: '0 0 8px 0',
              letterSpacing: '-0.025em',
              lineHeight: '1.2'
            }}>
              Choose Your{' '}
              <span style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Ordering Method
              </span>
            </h3>
            <p style={{
              fontSize: window.innerWidth <= 768 ? '12px' : '14px',
              color: '#6b7280',
              margin: '0 auto',
              lineHeight: '1.4'
            }}>
              Select your preferred way to order delicious food
            </p>
          </div>

          {/* Clean 2x2 Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)',
            gap: window.innerWidth <= 768 ? '12px' : '16px',
            maxWidth: window.innerWidth <= 768 ? '100%' : '600px',
            margin: '0 auto'
          }}>
            {options.slice(0, 4).map((option, index) => (
              <div
                key={option.id}
                style={{
                  cursor: 'pointer',
                  transition: `all 1s ease ${0.7 + index * 0.1}s`,
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
                }}
                onClick={option.action}
              >
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  transform: 'scale(1)',
                  position: 'relative',
                  overflow: 'hidden',
                  height: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }} onMouseEnter={(e) => {
                  if (window.innerWidth > 768) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  }
                }} onMouseLeave={(e) => {
                  if (window.innerWidth > 768) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }
                }}>
                  {/* Top Section - Icon and Title */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: option.color,
                      flexShrink: 0
                    }}>
                      {typeof option.icon === 'function' ? (
                        <option.icon />
                      ) : (
                        <option.icon size={18} color="white" />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: '700',
                        color: '#1f2937',
                        margin: '0 0 2px 0',
                        lineHeight: '1.2'
                      }}>
                        {option.title}
                      </h4>
                      <p style={{
                        fontSize: '11px',
                        color: '#6b7280',
                        margin: 0,
                        lineHeight: '1.3'
                      }}>
                        {option.description}
                      </p>
                    </div>
                  </div>

                  {/* Bottom Section - Action Button */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: 'auto'
                  }}>
                    <span style={{
                      color: '#f97316',
                      fontWeight: '600',
                      fontSize: '12px'
                    }}>
                      {option.id <= 3 ? 'Order Now' : option.id === 6 ? 'View Menu' : 'Coming Soon'}
                    </span>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <ArrowRight size={14} color="#f97316" />
                      {option.id <= 3 && (
                        <div style={{
                          width: '6px',
                          height: '6px',
                          background: '#10b981',
                          borderRadius: '50%'
                        }}></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Options Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)',
            gap: window.innerWidth <= 768 ? '12px' : '16px',
            maxWidth: window.innerWidth <= 768 ? '100%' : '600px',
            margin: '16px auto 0 auto'
          }}>
            {options.slice(4).map((option, index) => (
              <div
                key={option.id}
                style={{
                  cursor: 'pointer',
                  transition: `all 1s ease ${0.9 + index * 0.1}s`,
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
                }}
                onClick={option.action}
              >
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  transform: 'scale(1)',
                  position: 'relative',
                  overflow: 'hidden',
                  height: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }} onMouseEnter={(e) => {
                  if (window.innerWidth > 768) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  }
                }} onMouseLeave={(e) => {
                  if (window.innerWidth > 768) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }
                }}>
                  {/* Top Section - Icon and Title */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: option.color,
                      flexShrink: 0
                    }}>
                      {typeof option.icon === 'function' ? (
                        <option.icon />
                      ) : (
                        <option.icon size={18} color="white" />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: '700',
                        color: '#1f2937',
                        margin: '0 0 2px 0',
                        lineHeight: '1.2'
                      }}>
                        {option.title}
                      </h4>
                      <p style={{
                        fontSize: '11px',
                        color: '#6b7280',
                        margin: 0,
                        lineHeight: '1.3'
                      }}>
                        {option.description}
                      </p>
                    </div>
                  </div>

                  {/* Bottom Section - Action Button */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: 'auto'
                  }}>
                    <span style={{
                      color: '#f97316',
                      fontWeight: '600',
                      fontSize: '12px'
                    }}>
                      {option.id <= 3 ? 'Order Now' : option.id === 6 ? 'View Menu' : 'Coming Soon'}
                    </span>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <ArrowRight size={14} color="#f97316" />
                      {option.id <= 3 && (
                        <div style={{
                          width: '6px',
                          height: '6px',
                          background: '#10b981',
                          borderRadius: '50%'
                        }}></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Content Section */}
      <section style={{
        padding: window.innerWidth <= 768 ? '40px 16px' : '60px 16px',
        background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 50%, #f9fafb 100%)',
        position: 'relative'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: window.innerWidth <= 768 ? '32px' : '48px',
            transition: 'all 1s ease 1s',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
          }}>
            <h3 style={{
              fontSize: window.innerWidth <= 768 ? '24px' : '32px',
              fontWeight: '700',
              color: '#1f2937',
              margin: '0 0 16px 0',
              letterSpacing: '-0.025em',
              lineHeight: '1.2'
            }}>
              Why Choose{' '}
              <span style={{
                background: 'linear-gradient(135deg, #22c55e 0%, #10b981 50%, #14b8a6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                B.Tech Bawarchi?
              </span>
            </h3>
            <p style={{
              fontSize: window.innerWidth <= 768 ? '14px' : '16px',
              color: '#6b7280',
              maxWidth: window.innerWidth <= 768 ? '100%' : '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              We're committed to delivering the most exceptional dining experience with premium quality and authentic flavors.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: window.innerWidth <= 768 ? '24px' : '32px'
          }}>
            {[
              { icon: ChefHat, title: 'Authentic Recipes', description: 'Traditional Indian recipes passed down through generations with premium ingredients', color: 'linear-gradient(135deg, #22c55e 0%, #10b981 100%)', delay: '1.1s' },
              { icon: Clock, title: 'Fresh & Fast', description: 'Fresh ingredients cooked to order for maximum flavor and premium quality', color: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)', delay: '1.2s' },
              { icon: Users, title: 'Customer First', description: 'Dedicated to providing exceptional service and premium satisfaction', color: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)', delay: '1.3s' }
            ].map((feature, index) => (
              <div key={index} style={{
                textAlign: 'center',
                transition: `all 1s ease ${feature.delay}`,
                opacity: isLoaded ? 1 : 0,
                transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
              }}>
                <div style={{
                  width: window.innerWidth <= 768 ? '60px' : '80px',
                  height: window.innerWidth <= 768 ? '60px' : '80px',
                  borderRadius: window.innerWidth <= 768 ? '16px' : '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: window.innerWidth <= 768 ? '0 auto 20px auto' : '0 auto 24px auto',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  background: feature.color,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }} onMouseEnter={(e) => {
                  if (window.innerWidth > 768) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }
                }} onMouseLeave={(e) => {
                  if (window.innerWidth > 768) {
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}>
                  <feature.icon size={window.innerWidth <= 768 ? 28 : 36} color="white" />
                </div>
                <h4 style={{
                  fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: '0 0 12px 0'
                }}>{feature.title}</h4>
                <p style={{
                  color: '#6b7280',
                  lineHeight: '1.6',
                  fontSize: window.innerWidth <= 768 ? '14px' : '16px'
                }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location Map Section */}
      <section style={{
        background: 'linear-gradient(135deg, #f9fafb 0%, #ffffff 100%)',
        padding: window.innerWidth <= 768 ? '60px 16px' : '80px 16px',
        borderTop: '1px solid #e5e7eb',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: window.innerWidth <= 768 ? '40px' : '48px',
            transition: 'all 1s ease 1.5s',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
          }}>
            <h3 style={{
              fontSize: window.innerWidth <= 768 ? '24px' : '32px',
              fontWeight: '700',
              color: '#1f2937',
              margin: '0 0 16px 0',
              letterSpacing: '-0.025em',
              lineHeight: '1.2'
            }}>
              Find Us{' '}
              <span style={{
                background: 'linear-gradient(135deg, #ff6b35 0%, #f97316 50%, #ef4444 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Here
              </span>
            </h3>
            <p style={{
              fontSize: window.innerWidth <= 768 ? '14px' : '16px',
              color: '#6b7280',
              maxWidth: window.innerWidth <= 768 ? '100%' : '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Visit our cloud kitchen location or place an order online for fast home delivery.
            </p>
          </div>

          {/* Map Container */}
          <div style={{
            transition: 'all 1s ease 1.6s',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
          }}>
            <GoogleMapLocator />
          </div>

          {/* Location Info */}
          <div style={{
            marginTop: window.innerWidth <= 768 ? '24px' : '32px',
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr',
            gap: window.innerWidth <= 768 ? '24px' : '48px',
            transition: 'all 1s ease 1.7s',
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(40px)'
          }}>
            {/* Address */}
            <div style={{
              backgroundColor: 'white',
              padding: window.innerWidth <= 768 ? '20px' : '24px',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  backgroundColor: '#ff6b35',
                  borderRadius: '8px',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <MapPin size={20} color="white" />
                </div>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: 0
                }}>
                  Our Location
                </h4>
              </div>
              <p style={{
                color: '#6b7280',
                lineHeight: '1.6',
                fontSize: '14px',
                margin: '0 0 8px 0',
                fontWeight: '600'
              }}>
                B.Tech Bawarchi | Cloud Kitchen
              </p>
              <p style={{
                color: '#6b7280',
                lineHeight: '1.6',
                fontSize: '14px',
                margin: '0 0 8px 0'
              }}>
                Plot No. 74, KanakVrindvan Township<br/>
                Jaipur, Rajasthan, India
              </p>
            </div>

            {/* Contact Info */}
            <div style={{
              backgroundColor: 'white',
              padding: window.innerWidth <= 768 ? '20px' : '24px',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  backgroundColor: '#22c55e',
                  borderRadius: '8px',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Phone size={20} color="white" />
                </div>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: 0
                }}>
                  Contact Us
                </h4>
              </div>
              <p style={{
                color: '#6b7280',
                lineHeight: '1.6',
                fontSize: '14px',
                margin: '0 0 8px 0',
                fontWeight: '600'
              }}>
                Quick Support
              </p>
              <p style={{
                color: '#6b7280',
                lineHeight: '1.6',
                fontSize: '14px',
                margin: '0 0 12px 0'
              }}>
                Available 24/7 for orders and support<br/>
                WhatsApp OTP login for quick access
              </p>
              <button
                onClick={handleDirectOrder}
                style={{
                  background: 'linear-gradient(135deg, #ff6b35 0%, #f97316 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 107, 53, 0.3)';
                }}
              >
                <ArrowRight size={16} />
                Order Now
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 50%, #ffffff 100%)',
        color: '#1f2937',
        padding: window.innerWidth <= 768 ? '40px 16px' : '60px 16px',
        borderTop: '1px solid #e5e7eb'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: window.innerWidth <= 768 ? '32px' : '48px',
            marginBottom: '32px'
          }}>
            {/* Brand Section */}
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  padding: '8px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)'
                }}>
                  <ChefHat size={24} color="white" />
                </div>
                <h4 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  margin: 0
                }}>B.Tech Bawarchi</h4>
              </div>
              <p style={{
                color: '#6b7280',
                lineHeight: '1.6',
                fontSize: '14px',
                marginBottom: '20px'
              }}>
                Serving premium authentic Indian cuisine with love and tradition since our inception.
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }} onMouseEnter={(e) => {
                  if (window.innerWidth > 768) {
                    e.currentTarget.style.background = '#f97316';
                    e.currentTarget.style.borderColor = '#f97316';
                  }
                }} onMouseLeave={(e) => {
                  if (window.innerWidth > 768) {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }
                }}>
                  <Phone size={18} color="#6b7280" />
                </div>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }} onMouseEnter={(e) => {
                  if (window.innerWidth > 768) {
                    e.currentTarget.style.background = '#f97316';
                    e.currentTarget.style.borderColor = '#f97316';
                  }
                }} onMouseLeave={(e) => {
                  if (window.innerWidth > 768) {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }
                }}>
                  <MapPin size={18} color="#6b7280" />
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h5 style={{
                fontSize: '16px',
                fontWeight: '700',
                marginBottom: '16px',
                color: '#f97316'
              }}>Quick Links</h5>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                color: '#6b7280'
              }}>
                {[
                  { name: 'About Us', path: '/about' },
                  { name: 'Contact Us', path: '/contact' },
                  { name: 'Food Menu', path: '/menu' },
                  { name: 'Order Now', path: '/order' }
                ].map((link, index) => (
                  <li key={index} style={{ marginBottom: '8px' }}>
                    <Link to={link.path} style={{
                      color: '#6b7280',
                      fontSize: '14px',
                      fontWeight: '500',
                      textDecoration: 'none',
                      transition: 'color 0.3s ease',
                      display: 'block',
                      cursor: 'pointer',
                      padding: '4px 0',
                      position: 'relative',
                      zIndex: 1
                    }} onMouseEnter={(e) => {
                      if (window.innerWidth > 768) {
                        e.currentTarget.style.color = '#f97316';
                      }
                    }} onMouseLeave={(e) => {
                      if (window.innerWidth > 768) {
                        e.currentTarget.style.color = '#6b7280';
                      }
                    }}>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h5 style={{
                fontSize: '16px',
                fontWeight: '700',
                marginBottom: '16px',
                color: '#f97316'
              }}>Legal</h5>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                color: '#6b7280'
              }}>
                {[
                  { name: 'Privacy Policy', path: '/privacy' },
                  { name: 'Terms of Service', path: '/terms' },
                  { name: 'Refund Policy', path: '/contact' },
                  { name: 'Delivery Policy', path: '/contact' }
                ].map((link, index) => (
                  <li key={index} style={{ marginBottom: '8px' }}>
                    <Link to={link.path} style={{
                      color: '#6b7280',
                      fontSize: '14px',
                      fontWeight: '500',
                      textDecoration: 'none',
                      transition: 'color 0.3s ease',
                      display: 'block',
                      cursor: 'pointer',
                      padding: '4px 0',
                      position: 'relative',
                      zIndex: 1
                    }} onMouseEnter={(e) => {
                      if (window.innerWidth > 768) {
                        e.currentTarget.style.color = '#f97316';
                      }
                    }} onMouseLeave={(e) => {
                      if (window.innerWidth > 768) {
                        e.currentTarget.style.color = '#6b7280';
                      }
                    }}>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h5 style={{
                fontSize: '16px',
                fontWeight: '700',
                marginBottom: '16px',
                color: '#f97316'
              }}>Contact Info</h5>
              <div style={{ color: '#6b7280' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                  fontSize: '14px'
                }}>
                  <Phone size={16} color="#f97316" />
                  <span>+91 8107436861</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                  fontSize: '14px'
                }}>
                  <MapPin size={16} color="#f97316" />
                  <span>Jaipur, India</span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px'
                }}>
                  <Clock size={16} color="#f97316" />
                  <span>11:00 AM - 11:00 PM</span>
                </div>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div style={{
            borderTop: '1px solid #e5e7eb',
            paddingTop: '16px',
            textAlign: 'center',
            color: '#6b7280',
            fontSize: '12px'
          }}>
            <p style={{ margin: 0 }}>&copy; 2024 B.Tech Bawarchi. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default LandingPageNew;
